import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["debug", "countdown"]

  static values = {
    timeout: Number,
    warning: Number,
    pingInterval: Number
  }

  connect() {

    // console.log("Session timeout controller connected")


    const currentUserId = document.querySelector('meta[name="current-user-id"]')?.content
    if (!currentUserId) {
      return
    }

    this.lastActivityAt = Date.now()
    this.lastPingAt = Date.now()

    this.registerActivityListeners()
    this.resetInactivityTimer()
    this.startKeepaliveLoop()

    this.startCountdownTimer()

  }

  disconnect() {
    clearTimeout(this.inactivityTimer)
    clearTimeout(this.warningTimer)
    clearTimeout(this.logoutTimer)
    clearTimeout(this.debouncePingTimeout)
    clearInterval(this.keepaliveInterval)
    clearInterval(this.countdownInterval)
    this.removeActivityListeners()
  }

  registerActivityListeners() {
    this.activityHandler = this.handleUserActivity.bind(this)

    window.addEventListener("mousemove", this.activityHandler)
    window.addEventListener("keydown", this.activityHandler)
    window.addEventListener("click", this.activityHandler)
    window.addEventListener("scroll", this.activityHandler)
  }

  removeActivityListeners() {
    window.removeEventListener("mousemove", this.activityHandler)
    window.removeEventListener("keydown", this.activityHandler)
    window.removeEventListener("click", this.activityHandler)
    window.removeEventListener("scroll", this.activityHandler)
  }

  handleUserActivity() {
    // console.log("User activity detected.....")

    const now = Date.now()
    this.lastActivityAt = now

    // Cancel any pending debounce
    clearTimeout(this.debouncePingTimeout)

    // Debounce delay — how long after activity stops before we consider pinging
    const debounceDelay = 2000 // milliseconds

    this.debouncePingTimeout = setTimeout(() => {
      const pingThreshold = this.pingIntervalValue || 5 * 60 * 1000

      if (now - this.lastPingAt > pingThreshold) {
        // console.log("resetting session timeout")
        this.pingServer()
      }
    }, debounceDelay)

    this.resetInactivityTimer()
  }


  resetInactivityTimer() {
    clearTimeout(this.inactivityTimer)
    clearTimeout(this.warningTimer)
    clearTimeout(this.logoutTimer)

    // Start inactivity countdown
    this.inactivityTimer = setTimeout(() => {
      this.startSessionCountdown()
    }, this.timeoutValue - this.warningValue)
  }

  showWarning() {
    alert("Still with us? Just close this alert to keep your session going.")

    this.pingServer()
    this.resetInactivityTimer()

    // Reset timers
    // clearTimeout(this.warningTimer)
    // clearTimeout(this.logoutTimer)

    // this.connect()
  }

  startSessionCountdown() {
    // console.log("User inactive: starting session countdown")

    this.warningTimer = setTimeout(() => {
      this.showWarning()
    }, 0) // show warning immediately

    this.logoutTimer = setTimeout(() => {
      this.logout()
    }, this.warningValue)
  }

  isInIframe() {
    try { return window.self !== window.top } catch (_) { return true } // cross-origin
  }

  startKeepaliveLoop() {
    const interval = this.pingIntervalValue || 5 * 60 * 1000

    if (this.isInIframe()) {
      // Embedded: ping unconditionally
      this.pingServer() // kick once immediately
      this.keepaliveInterval = setInterval(() => this.pingServer(), interval)
      return
    }

    this.keepaliveInterval = setInterval(() => {
      const now = Date.now()
      const elapsed = now - this.lastActivityAt

      if (elapsed < interval) {
        this.pingServer()
      }
    }, interval)
  }

  pingServer() {
    // console.log("Pinging server to keep session alive: " + new Date().toISOString())
    fetch("/keepalive", { credentials: "include" })
    this.lastPingAt = Date.now()
  }

  logout() {
    fetch("/users/sign_out", {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
        "Content-Type": "application/json"
      },
      credentials: "same-origin"
    }).then(() => {
      window.location.href = "/"
    })
  }


  startCountdownTimer() {
    if (!this.hasCountdownTarget) return

    this.updateCountdown()

    this.countdownInterval = setInterval(() => {
      this.updateCountdown()
    }, 1000)
  }

  updateCountdown() {
    const timeLeft = this.getTimeLeftInSeconds()

    if (timeLeft <= 0) {
      this.countdownTarget.textContent = "EXPIRED"
      clearInterval(this.countdownInterval)
      return
    }

    const minutes = Math.floor(timeLeft / 60)
    const seconds = timeLeft % 60
    this.countdownTarget.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  getTimeLeftInSeconds() {
    const timeoutMs = this.timeoutValue
    const now = Date.now()
    const expiresAt = this.lastActivityAt + timeoutMs
    const secondsLeft = Math.floor((expiresAt - now) / 1000)
    return Math.max(0, secondsLeft)
  }


}
